import { create } from "zustand";
import {
  listGameTag,
  createGameTag,
  updateGameTag,
  deleteGameTag,
  type GameTagItem,
} from "@/services/game";

interface GameTagState {
  gameTags: GameTagItem[];
  loading: boolean;
  setGameTags: (gameTags: GameTagItem[]) => void;
  fetchGameTags: (force?: boolean) => Promise<GameTagItem[]>;
  addGameTag: (data: { name: string; sort: number }) => Promise<void>;
  editGameTag: (data: { id: number; name: string; sort: number }) => Promise<void>;
  removeGameTag: (id: number) => Promise<void>;
}

const normalizeGameTags = (
  response: { list: GameTagItem[] } | GameTagItem[],
): GameTagItem[] => {
  const gameTags = Array.isArray(response) ? response : response?.list || [];

  return [...gameTags].sort((first, second) => first.sort - second.sort);
};

export const useGameTagStore = create<GameTagState>((set, get) => ({
  gameTags: [],
  loading: false,

  setGameTags: (gameTags) => set({ gameTags }),

  fetchGameTags: async (force = false) => {
    const { gameTags, loading } = get();

    if (gameTags.length > 0 && !force) {
      return gameTags;
    }

    if (loading) {
      return gameTags;
    }

    set({ loading: true });

    try {
      const response = await listGameTag();
      const normalizedGameTags = normalizeGameTags(response);

      set({ gameTags: normalizedGameTags, loading: false });
      return normalizedGameTags;
    } catch (_error) {
      set({ loading: false });
      return [];
    }
  },

  addGameTag: async (data) => {
    set({ loading: true });
    try {
      await createGameTag(data);
      const response = await listGameTag();
      const normalizedGameTags = normalizeGameTags(response);
      set({ gameTags: normalizedGameTags, loading: false });
    } catch (error) {
      set({ loading: false });
      throw error;
    }
  },

  editGameTag: async (data) => {
    set({ loading: true });
    try {
      await updateGameTag(data);
      const response = await listGameTag();
      const normalizedGameTags = normalizeGameTags(response);
      set({ gameTags: normalizedGameTags, loading: false });
    } catch (error) {
      set({ loading: false });
      throw error;
    }
  },

  removeGameTag: async (id) => {
    set({ loading: true });
    try {
      await deleteGameTag(id);
      const response = await listGameTag();
      const normalizedGameTags = normalizeGameTags(response);
      set({ gameTags: normalizedGameTags, loading: false });
    } catch (error) {
      set({ loading: false });
      throw error;
    }
  },
}));
